import { VStack } from "components/Stack";
import useAccountForm from "../hook";
import Button from "components/formElements/Button/Button";
import PasswordRequirement from "components/PasswordRequirement";
import { useMemo, useState } from "react";
import TalaeraSpinner from "components/TalaeraSpinner/TalaeraSpinner";
import queryString from "querystring";
import { populateUserSession, signIn, verifyUser } from "services/authUtil";
import { getErrorComponents } from "services/util";
import { PasswordInput } from "../../../../components/PasswordInput/PasswordInput";
import { Trans, useTranslation } from "react-i18next";

type Props = {
  onNextStep: () => void;
  location: { [key: string]: any };
};

const initData = {
  password: "",
  confirmPassword: "",
};

function CreateAccount(props: Props) {
  const {
    account,
    setAccount,
    errors,
    touched,
    onTouched,
    setTouched,
    passwordStatus,
    onValidate,
  } = useAccountForm(initData);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<any>("");
  const { t } = useTranslation();

  const isValidForm = useMemo(
    () => onValidate(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [account.password, account.confirmPassword]
  );

  const hdChange = (event: any, name: string) => {
    let value = event.target?.value;
    setAccount(prev => ({ ...prev, [name]: value }));
  };

  const handleBlur = (e: any) => {
    onTouched(e.target?.name);
  };

  const onError = (error: any) => {
    setErr(error);
    setLoading(false);
  };

  const hdSubmit = () => {
    setTouched({
      password: true,
      confirmPassword: true,
    });

    const isValidated = onValidate();
    if (isValidated) {
      setLoading(true);
      setErr(null);

      let parseData = queryString.parse((props.location?.search || "").substring(1));

      verifyUser({ token: parseData.token, passwd: account.password }).then(
        (rs: { [key: string]: any }) => {
          if (account.password) {
            signIn(
              {
                email: rs.email,
                password: account.password,
              },
              () => {
                populateUserSession(() => {
                  setAccount({ password: "", confirmPassword: "" });
                  props.onNextStep();
                  setLoading(false);
                }, onError);
              },
              onError
            );
          }
        },
        onError
      );
    }
  };

  return (
    <div className={"create-account-step"}>
      <h1 className={"step-title"}>{t("Signup.Create an account")}</h1>

      <div className={"content"}>
        <div>
          {err && getErrorComponents(err)}

          <VStack gap={24}>
            <PasswordInput
              className="full-width"
              name="password"
              label={t("Signup.Password")}
              variant="outlined"
              type="password"
              value={account.password}
              error={!!(errors.password && touched.password)}
              helperText={touched.password && errors.password}
              onBlur={handleBlur}
              onChange={(e: any) => hdChange(e, "password")}
            />

            <PasswordInput
              className="full-width"
              name="confirmPassword"
              label={t("Signup.Confirm Password")}
              variant="outlined"
              type="password"
              value={account.confirmPassword}
              error={!!(errors.confirmPassword && touched.confirmPassword)}
              helperText={touched.confirmPassword && errors.confirmPassword}
              onBlur={handleBlur}
              onChange={(e: any) => hdChange(e, "confirmPassword")}
            />
          </VStack>

          {account.password && <PasswordRequirement passwordStatus={passwordStatus} />}

          <div className="legal-panel">
            <p>
              <Trans i18nKey={"Signup.byJoining"}>
                By joining Talaera, you agree to Talaera's
                <br />
                <a
                  href="https://www.talaera.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>{" "}
                and{" "}
                <a
                  href="https://www.talaera.com/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Service
                </a>
              </Trans>
            </p>
          </div>

          {loading && (
            <div className="spinner-container">
              <TalaeraSpinner />
            </div>
          )}

          {!loading && (
            <Button disabled={!isValidForm} className={"btn"} onClick={hdSubmit}>
              {t("Signup.Create account")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default CreateAccount;
